import { Link } from 'react-router-dom';
import type { GlobalSearchResult } from '@whinerp/client';
import { useT } from '../../app/i18n.js';
import { recordHref } from './record-href.js';

/**
 * Search results, grouped by object (F-020).
 *
 * ONE component, rendered by the overlay and by the results page, so the two
 * cannot come to disagree about how a result reads.
 *
 * The matched substring is marked. A list of names that all look unrelated to
 * the query is a list people stop trusting - the highlight is what says WHY
 * each row is there.
 */
export function SearchResults({
  groups,
  onNavigate,
}: {
  groups: GlobalSearchResult['groups'];
  onNavigate?: () => void;
}): JSX.Element {
  const t = useT();
  void t;

  return (
    <div data-search-results className="flex flex-col gap-4">
      {groups.map((group) => (
        <section key={group.objectKey} data-search-group={group.objectKey} className="flex flex-col gap-1">
          <p className="text-micro uppercase tracking-label text-ink-label">{group.label}</p>
          <ul className="flex flex-col">
            {group.hits.map((hit) => (
              <li key={`${hit.objectKey}:${hit.recordId}`}>
                <Link
                  to={recordHref(hit.objectKey, hit.recordId)}
                  onClick={onNavigate}
                  data-search-hit={hit.recordId}
                  className="flex flex-wrap items-baseline gap-2 rounded-control px-2 py-1.5 text-body text-ink hover:bg-sunken"
                >
                  <Highlighted text={hit.title} start={hit.matchStart} length={hit.matchLength} />
                  {hit.detail ? <span className="text-meta text-ink-meta">{hit.detail}</span> : null}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * The matched run, marked.
 *
 * Only when the position actually falls inside this string: the match may have
 * been in a different field from the title, and slicing on an offset that
 * belongs to another value would highlight the wrong letters.
 */
function Highlighted({
  text,
  start,
  length,
}: {
  text: string;
  start: number;
  length: number;
}): JSX.Element {
  const within = start >= 0 && start + length <= text.length;
  if (!within) return <span>{text}</span>;
  return (
    <span>
      {text.slice(0, start)}
      <mark data-search-match className="bg-bronze-tint text-ink">
        {text.slice(start, start + length)}
      </mark>
      {text.slice(start + length)}
    </span>
  );
}
